/**
 * Command bridge — mirrors extension `CommandContribution`s into the editor's
 * central command registry (`lib/commands`) so they appear in the command
 * palette and participate in keyboard-shortcut binding automatically.
 *
 * The bridge subscribes to the contribution registry and reconciles the command
 * registry on every change. Extension command ids are namespaced as
 * `ext:<extensionId>:<commandId>` to avoid collisions with built-in commands.
 */
import { useEffect } from "react";
import {
    bindShortcut,
    getAllCommands,
    registerCommand,
    unregisterCommand,
} from "../lib/commands";
import { contributionRegistry } from "./contribution-registry";

const EXT_PREFIX = "ext:";

function bridgeCommandId(extensionId: string, commandId: string): string {
    return `${EXT_PREFIX}${extensionId}:${commandId}`;
}

/** Reconcile the central command registry with the current contributions. */
export function syncExtensionCommands(): void {
    const desired = new Map<string, { label: string; category?: string; shortcut?: string; handler: () => void | Promise<void> }>();
    for (const { extensionId, command } of contributionRegistry.commands()) {
        desired.set(bridgeCommandId(extensionId, command.id), {
            label: command.title,
            category: command.category,
            shortcut: command.shortcut,
            handler: command.handler,
        });
    }

    // Remove stale bridged commands no longer contributed.
    for (const cmd of getAllCommands()) {
        if (cmd.id.startsWith(EXT_PREFIX) && !desired.has(cmd.id)) {
            unregisterCommand(cmd.id);
        }
    }

    // Register / update current commands.
    for (const [id, def] of desired) {
        registerCommand({
            id,
            label: def.label,
            category: def.category,
            shortcut: def.shortcut,
            execute: () => {
                void def.handler();
            },
        });
        if (def.shortcut) {
            bindShortcut(def.shortcut, id);
        }
    }
}

/**
 * React hook that keeps the command registry in sync with extension command
 * contributions for as long as it is mounted. Mount once near the app shell root.
 */
export function useExtensionCommandBridge(): void {
    useEffect(() => {
        syncExtensionCommands();
        const off = contributionRegistry.subscribe(syncExtensionCommands);
        return () => {
            off();
            // Clean up bridged commands on unmount.
            for (const cmd of getAllCommands()) {
                if (cmd.id.startsWith(EXT_PREFIX)) unregisterCommand(cmd.id);
            }
        };
    }, []);
}
